import { defaultPasses, normalizePasses, type RevisionPass } from '../../utils/revisionPasses'
import { CRUTCH_DEFAULTS } from '../../utils/editorModeTuning'
import { normalizeHabitWord } from '../../utils/habits'

/**
 * Editor Mode: a second way of seeing the page, for revision. It changes the
 * page's look (so the eye stops reading what it remembers) and adds lenses
 * that expose sentence structure.
 */
export type EditorModePalette = 'auto' | 'galley' | 'legal' | 'night' | 'custom'

export interface EditorModeSettings {
  /** 'auto' picks a face unlike the drafting font. */
  font: string
  /** Size relative to the drafting size. */
  fontScale: number
  lineHeight: number
  /** In em. */
  letterSpacing: number
  /** In em. */
  wordSpacing: number
  /** Column width in ch. */
  width: number
  palette: EditorModePalette
  customBg: string
  customText: string
  /** Break the page so each sentence starts on its own line. */
  sentenceLines: boolean
  /** Show each sentence's word count. */
  sentenceCounts: boolean
  /** Mark the first word of every sentence, and overused or repeated ones. */
  openers: boolean
  /** Sentences at least this many words long count as long. */
  longSentence: number
  /** Habit lenses (Free mode; a pass carries its own). */
  echoes: boolean
  filterWords: boolean
  crutchWords: boolean
  dialogueTags: boolean
  paragraphWalls: boolean
  /** The writer's crutch words and phrases. */
  crutchList: string[]
  /** Flag every -ly adverb as a crutch word. */
  crutchLyAdverbs: boolean
  /** Words and phrases no habit lens flags. */
  neverFlag: string[]
  /** Habit lenses that also look inside quotations. */
  habitsInDialogue: { echoes: boolean; filterWords: boolean; crutchWords: boolean }
  /** The revision plan: every pass, in order. */
  passes: RevisionPass[]
}

export const DEFAULT_EDITOR_MODE_SETTINGS: EditorModeSettings = {
  font: 'auto',
  fontScale: 1.1,
  lineHeight: 2,
  letterSpacing: 0.01,
  wordSpacing: 0.1,
  width: 54,
  palette: 'auto',
  customBg: '#ffffff',
  customText: '#111111',
  sentenceLines: false,
  sentenceCounts: false,
  openers: false,
  longSentence: 30,
  echoes: false,
  filterWords: false,
  crutchWords: false,
  dialogueTags: false,
  paragraphWalls: false,
  crutchList: [...CRUTCH_DEFAULTS],
  crutchLyAdverbs: false,
  neverFlag: [],
  habitsInDialogue: { echoes: false, filterWords: false, crutchWords: false },
  passes: defaultPasses(),
}

const HABIT_WORD_LIST_MAX = 300

function habitWordList(raw: unknown, fallback: string[]): string[] {
  if (!Array.isArray(raw)) return [...fallback]
  const seen = new Set<string>()
  for (const item of raw) {
    if (typeof item !== 'string') continue
    const word = normalizeHabitWord(item).slice(0, 40)
    if (word) seen.add(word)
    if (seen.size >= HABIT_WORD_LIST_MAX) break
  }
  return [...seen]
}

export function normalizeEditorModeSettings(raw: any): EditorModeSettings {
  const d = DEFAULT_EDITOR_MODE_SETTINGS
  const num = (value: unknown, min: number, max: number, fallback: number) => {
    const n = Number(value)
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
  }
  const color = (value: unknown, fallback: string) =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback
  return {
    font: typeof raw?.font === 'string' && raw.font.trim() ? raw.font : d.font,
    fontScale: num(raw?.fontScale, 0.7, 1.8, d.fontScale),
    lineHeight: num(raw?.lineHeight, 1, 3.2, d.lineHeight),
    letterSpacing: num(raw?.letterSpacing, -0.05, 0.2, d.letterSpacing),
    wordSpacing: num(raw?.wordSpacing, -0.1, 0.8, d.wordSpacing),
    width: Math.round(num(raw?.width, 25, 140, d.width)),
    palette: ['auto', 'galley', 'legal', 'night', 'custom'].includes(raw?.palette) ? raw.palette : d.palette,
    customBg: color(raw?.customBg, d.customBg),
    customText: color(raw?.customText, d.customText),
    sentenceLines: raw?.sentenceLines === true,
    sentenceCounts: raw?.sentenceCounts === true,
    openers: raw?.openers === true,
    longSentence: Math.round(num(raw?.longSentence, 10, 80, d.longSentence)),
    echoes: raw?.echoes === true,
    filterWords: raw?.filterWords === true,
    crutchWords: raw?.crutchWords === true,
    dialogueTags: raw?.dialogueTags === true,
    paragraphWalls: raw?.paragraphWalls === true,
    crutchList: habitWordList(raw?.crutchList, CRUTCH_DEFAULTS),
    crutchLyAdverbs: raw?.crutchLyAdverbs === true,
    neverFlag: habitWordList(raw?.neverFlag, []),
    habitsInDialogue: {
      echoes: raw?.habitsInDialogue?.echoes === true,
      filterWords: raw?.habitsInDialogue?.filterWords === true,
      crutchWords: raw?.habitsInDialogue?.crutchWords === true,
    },
    passes: normalizePasses(raw?.passes),
  }
}
